import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { useSettings } from "./SettingsContext";

const CartContext = createContext(null);

// v2 key forces a fresh cart after the JMD-first migration
// (old carts stored USD prices and would produce wrong totals)
const CART_KEY = "c4t_cart_v2";

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const { exchangeRate } = useSettings();
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(CART_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  // Re-price cart items from the live catalogue on load so the cart never shows a stale price
  useEffect(() => {
    if (!items.length) return;
    let alive = true;
    (async () => {
      try {
        const [pr, bd] = await Promise.all([
          api.get("/products?limit=1000").then((r) => r.data).catch(() => []),
          api.get("/bundles").then((r) => r.data).catch(() => []),
        ]);
        const list = Array.isArray(pr) ? pr : (pr.items || pr.products || []);
        const pmap = {}; list.forEach((p) => { pmap[p.id] = p; });
        const bmap = {}; (Array.isArray(bd) ? bd : []).forEach((b) => { bmap[b.id] = b; });
        if (!alive) return;
        setItems((prev) => prev.map((it) => {
          if (it.is_bundle) {
            const b = bmap[it.product_id];
            if (b && Number(b.price) > 0) { const np = Number(b.price); return np !== Number(it.price) ? { ...it, price: np, price_was: Number(it.price), price_changed: true } : { ...it, price_changed: false }; } return it;
          }
          const p = pmap[it.product_id];
          if (!p) return it;
          const eff = Number(p.sale_price) > 0 ? Number(p.sale_price) : Number(p.price);
          if (Number.isFinite(eff) && eff > 0) { return eff !== Number(it.price) ? { ...it, price: eff, price_was: Number(it.price), price_changed: true } : { ...it, price_changed: false }; } return it;
        }));
      } catch (e) {}
    })();
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  }, [items]);

  const syncTimer = useRef(null);
  useEffect(() => {
    if (!user) return;
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      api.post("/cart/sync", { items }).catch(() => {});
    }, 800);
    return () => syncTimer.current && clearTimeout(syncTimer.current);
  }, [items, user]);

  useEffect(() => {
    if (user && items.length > 0) {
      api.post("/cart/sync", { items }).catch(() => {});
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const addItem = (product, qty = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.product_id === product.id);
      const stock = typeof product.stock === "number" ? product.stock : Infinity;
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: Math.min(stock, next[idx].quantity + qty) };
        return next;
      }
      const rate = exchangeRate || 160;
      const activePrice = product.sale_price > 0 ? product.sale_price : product.price;
      // All prices are stored in JMD. Convert USD-priced products at the current rate.
      const priceJMD = (product.currency || "JMD") === "USD"
        ? Math.round((activePrice || 0) * rate)
        : (activePrice || 0);
      return [...prev, {
        product_id: product.id,
        name: product.name,
        price: priceJMD,   // JMD
        image: product.image,
        quantity: Math.min(stock, qty),
        stock,
        ...(product.is_bundle ? { is_bundle: true, bundle_items: product.bundle_items || [] } : {}),
      }];
    });
  };

  const updateQty = (product_id, qty) => {
    setItems((prev) => prev.map((i) => {
      if (i.product_id !== product_id) return i;
      const max = typeof i.stock === "number" ? i.stock : Infinity;
      return { ...i, quantity: Math.min(max, Math.max(1, qty)) };
    }));
  };

  const removeItem = (product_id) => setItems((prev) => prev.filter((i) => i.product_id !== product_id));
  const clearCart = () => {
    setItems([]);
    if (user) api.delete("/cart").catch(() => {});
  };

  // subtotal is in JMD
  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const count = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, updateQty, removeItem, clearCart, subtotal, count }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);